import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Button, Select, Input, Space, message } from 'antd';
import { DownloadOutlined, DownOutlined, CloseOutlined, MailOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from 'Utils/api';
import { useIsMobile } from 'Utils/useIsMobile';
import { downloadCsv } from 'Utils/downloadCsv';
import MobileListLayout from '../Components/MobileList/MobileListLayout';
import MobileCard from '../Components/MobileList/MobileCard';
import OptionSheet from '../Components/MobileList/OptionSheet';
import { Chip } from '../Components/ProductMobileList/ProductMobileList.style';

interface Subscriber {
  id: string;
  email: string;
  status: 'PENDING' | 'CONFIRMED';
  language: string;
  consentedAt: string;
  confirmedAt: string | null;
  sources: { source: string; createdAt: string }[];
}

const STATUS: Record<string, { label: string; color: string }> = {
  CONFIRMED: { label: 'Bestätigt', color: 'green' },
  PENDING: { label: 'Unbestätigt', color: 'orange' },
};

const PAGE_SIZE = 50;
const formatDate = (value: string | null) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '–');

const NewsletterPage: React.FC = () => {
  const isMobile = useIsMobile();
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sources, setSources] = useState<{ source: string; count: number }[]>([]);
  const [source, setSourceState] = useState('');
  const [status, setStatusState] = useState('');
  const [search, setSearchState] = useState('');
  const [sheet, setSheet] = useState<'source' | 'status' | null>(null);

  useEffect(() => {
    apiClient.get('/api/admin/newsletter-subscribers/sources')
      .then((res) => setSources(res.data.sources || []))
      .catch(() => undefined);
  }, []);

  // Filters restart at page 1 in the same render, so mobile never appends a page of the old filter
  const setSource = (value: string) => { setSourceState(value); setPage(1); };
  const setStatus = (value: string) => { setStatusState(value); setPage(1); };
  const setSearch = (value: string) => { setSearchState(value); setPage(1); };

  useEffect(() => { setPage(1); }, [isMobile]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/admin/newsletter-subscribers', {
        params: { page, take: PAGE_SIZE, ...(source ? { source } : {}), ...(status ? { status } : {}), ...(search ? { search } : {}) },
      });
      const list: Subscriber[] = res.data.subscribers || [];
      // Mobile appends pages for "load more"
      setSubscribers((prev) => (isMobile && page > 1 ? [ ...prev, ...list ] : list));
      setCount(res.data.meta?.count || 0);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden der Newsletter-Anmeldungen');
    } finally {
      setLoading(false);
    }
  }, [page, source, status, search, isMobile]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get('/api/admin/newsletter-subscribers/export', { params: source ? { source } : {} });
      downloadCsv(res.data.csv, res.data.filename);
      message.success('Newsletter-Liste heruntergeladen (nur bestätigte Adressen)');
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const exportButton = (
    <Button icon={<DownloadOutlined />} onClick={exportCsv} loading={exporting} data-testid="newsletter-export">
      {isMobile ? 'Export' : `Export${source ? ` (${source})` : ''} – nur bestätigte`}
    </Button>
  );
  const sourceTags = (s: Subscriber) => s.sources.map((x) => <Tag key={x.source}>{x.source}</Tag>);
  const sourceOptions = [ { value: '', label: 'Alle Quellen' }, ...sources.map((s) => ({ value: s.source, label: `${s.source} (${s.count})` })) ];
  const statusOptions = [ { value: '', label: 'Alle Status' }, ...Object.entries(STATUS).map(([ value, s ]) => ({ value, label: s.label })) ];

  if (isMobile) {
    const filtered = !!(source || status || search);
    return (
      <>
        <MobileListLayout
          title="Newsletter"
          action={exportButton}
          search={search}
          onSearch={setSearch}
          searchPlaceholder="E-Mail suchen …"
          filters={(
            <>
              {source
                ? <Chip type="button" onClick={() => setSource('')}><span>{source}</span><CloseOutlined /></Chip>
                : <Button onClick={() => setSheet('source')}>Quelle <DownOutlined /></Button>}
              {status
                ? <Chip type="button" onClick={() => setStatus('')}><span>{STATUS[status].label}</span><CloseOutlined /></Chip>
                : <Button onClick={() => setSheet('status')}>Status <DownOutlined /></Button>}
            </>
          )}
          count={`${count} ${count === 1 ? 'Adresse' : 'Adressen'}`}
          loading={loading}
          isEmpty={subscribers.length === 0}
          emptyTitle="Keine Anmeldungen gefunden"
          emptyText={filtered ? 'Keine Anmeldung entspricht den aktuellen Filtern.' : 'Anmeldungen aus Gewinnspielen erscheinen hier.'}
          onReset={filtered ? () => { setSource(''); setStatus(''); setSearch(''); } : undefined}
          hasMore={subscribers.length < count}
          onLoadMore={() => setPage((p) => p + 1)}
        >
          {subscribers.map((s) => (
            <MobileCard
              key={s.id}
              testId={`mobile-newsletter-card-${s.id}`}
              overline={`Einwilligung ${formatDate(s.consentedAt)}`}
              title={s.email}
              highlight={<><Tag color={STATUS[s.status].color}>{STATUS[s.status].label}</Tag>{sourceTags(s)}</>}
              meta={s.confirmedAt ? `Bestätigt ${formatDate(s.confirmedAt)} · ${s.language.toUpperCase()}` : s.language.toUpperCase()}
              onOpen={() => undefined}
              actions={[ { key: 'mail', label: 'E-Mail schreiben', icon: <MailOutlined />, onClick: () => { window.location.href = `mailto:${s.email}`; } } ]}
            />
          ))}
        </MobileListLayout>
        <OptionSheet open={sheet === 'source'} title="Quelle" value={source} options={sourceOptions} onClose={() => setSheet(null)} onChange={(v) => { setSource(v); setSheet(null); }} />
        <OptionSheet open={sheet === 'status'} title="Status" value={status} options={statusOptions} onClose={() => setSheet(null)} onChange={(v) => { setStatus(v); setSheet(null); }} />
      </>
    );
  }

  const columns = [
    { title: 'E-Mail', dataIndex: 'email', key: 'email', render: (email: string) => <a href={`mailto:${email}`}>{email}</a> },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v: string) => <Tag color={STATUS[v].color}>{STATUS[v].label}</Tag> },
    { title: 'Quellen', key: 'sources', render: (_: any, s: Subscriber) => sourceTags(s) },
    { title: 'Sprache', dataIndex: 'language', key: 'language', render: (v: string) => v.toUpperCase() },
    { title: 'Einwilligung', dataIndex: 'consentedAt', key: 'consentedAt', render: formatDate },
    { title: 'Bestätigt', dataIndex: 'confirmedAt', key: 'confirmedAt', render: formatDate },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h1>Newsletter</h1>
        {exportButton}
      </div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Select value={source} onChange={setSource} options={sourceOptions} style={{ width: 240 }} data-testid="newsletter-source" />
        <Select value={status} onChange={setStatus} options={statusOptions} style={{ width: 160 }} />
        <Input.Search placeholder="E-Mail suchen" allowClear onSearch={setSearch} style={{ width: 260 }} />
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={subscribers}
        loading={loading}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: count, showSizeChanger: false, onChange: setPage, showTotal: (t) => `Gesamt: ${t} Adressen` }}
      />
    </div>
  );
};

export default NewsletterPage;
